// /batch/:id — batch detail with item status + download.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, batches, type BatchItem, type BatchSummary } from '../api.js';

const STATUS_STYLES: Record<BatchSummary['status'], string> = {
  pending: 'text-muted bg-soft/60',
  processing: 'text-ink bg-soft/60',
  completed: 'text-accent bg-accent/10',
  failed: 'text-accent bg-accent/10',
  cancelled: 'text-muted bg-soft/60',
};

const ITEM_STATUS_STYLES: Record<BatchItem['status'], string> = {
  pending: 'text-muted',
  directing: 'text-ink',
  rendering: 'text-ink',
  completed: 'text-accent',
  failed: 'text-accent',
};

export function BatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [batch, setBatch] = useState<BatchSummary | null>(null);
  const [items, setItems] = useState<BatchItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    if (!id) return;
    setError(null);
    try {
      const r = await batches.get(id);
      setBatch(r.batch);
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
    }
  }

  useEffect(() => {
    void refresh();
    const t = setInterval(() => {
      if (batch && (batch.status === 'pending' || batch.status === 'processing')) {
        void refresh();
      }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, batch?.status]);

  async function handleCancel() {
    if (!id || !batch) return;
    if (!confirm('Cancel this batch? In-flight items will finish.')) return;
    try {
      await batches.cancel(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Cancel failed.');
    }
  }

  if (!batch) {
    return (
      <div>
        <p className="text-muted text-sm">{error ?? 'loading…'}</p>
        <Link to="/batch" className="text-xs text-muted hover:text-ink mt-4 inline-block">
          ← back to batches
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link to="/batch" className="text-xs text-muted hover:text-ink mb-4 inline-block">
        ← back to batches
      </Link>

      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-mono text-2xl">{batch.id.slice(0, 8)}</h1>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_STYLES[batch.status]}`}>
              {batch.status}
            </span>
          </div>
          <p className="text-muted text-sm">
            {batch.completed_count}/{batch.item_count} items complete · director {batch.director_model}
            {batch.tts_provider ? ` · TTS ${batch.tts_provider}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {batch.status === 'completed' && (
            <a
              href={batches.downloadUrl(batch.id)}
              className="bg-accent text-parchment px-4 py-2 rounded-md text-sm hover:bg-accent/90"
            >
              Download manifest
            </a>
          )}
          {(batch.status === 'pending' || batch.status === 'processing') && (
            <button
              onClick={handleCancel}
              className="text-xs text-muted hover:text-accent px-3 py-2 border border-soft rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} className="border border-soft rounded-lg px-5 py-4 bg-white">
            <div className="flex items-baseline justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted">#{it.index + 1}</span>
                  <h2 className="font-serif text-base truncate">{it.title}</h2>
                  <span className={`text-[10px] uppercase tracking-wider ${ITEM_STATUS_STYLES[it.status]}`}>
                    {it.status}
                  </span>
                </div>
                {it.error && (
                  <div className="text-xs text-accent mt-1">{it.error}</div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {it.project_id && (
                  <Link
                    to={`/projects/${it.project_id}`}
                    className="text-xs text-muted hover:text-ink px-2 py-1"
                  >
                    Open project
                  </Link>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
